/**
 * Deep-link helpers for the acquisition entry point:
 *
 *     #/project/<projectId>/<sampleId>/<ts>?x=<px>&y=<px>
 *
 * `<ts>` is the acquisition timestamp exactly as it appears in the S3 keys
 * (2026-06-11T00-00-55Z — ISO 8601 with ':' replaced by '-' and the
 * milliseconds dropped), so a link can be built from an object key without
 * knowing anything about the database.
 *
 * This mirrors `apps/backend/src/s3/s3-layout.ts`; the two cannot share code
 * (the backend is not a frontend dependency), so keep them in step.
 */
import type { ImageModel } from '@/types/models';

/** A pixel in the raw, unrotated jp2 — the coordinate space the annotation
 *  tool works in. The viewer applies the sample's rotation on top. */
export type ViewerFocus = { x: number; y: number };

/** Format an acquisition date as the timestamp used in S3 keys and deep links. */
export function formatAcquisitionTimestamp(date: Date): string {
    return date
        .toISOString()
        .replace(/:/g, '-')
        .replace(/\.[0-9]{3}Z$/, 'Z');
}

/** Parse a deep-link timestamp back into a Date, or undefined. Both the key
 *  form (2026-06-11T00-00-55Z) and a plain ISO string are accepted. */
export function parseAcquisitionTimestamp(ts: string): Date | undefined {
    const iso = ts.replace(
        /^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})(?:[.-](\d{1,3}))?Z$/,
        '$1T$2:$3:$4Z',
    );
    const d = new Date(iso);
    return isNaN(d.getTime()) ? undefined : d;
}

/**
 * Index of the acquisition a deep link points at, or -1.
 *
 * An exact timestamp match wins; failing that the nearest acquisition in time
 * is used, so a link built from a slightly different rounding of the same
 * capture still lands on it instead of dropping the user on image 1.
 */
export function findAcquisitionIndex(
    images: ImageModel[],
    ts: string,
): number {
    const wanted = parseAcquisitionTimestamp(ts);
    if (!wanted) return -1;
    let best = -1;
    let bestDelta = Infinity;
    images.forEach((image, index) => {
        const date = new Date(image.acquisitionDate);
        if (isNaN(date.getTime())) return;
        if (formatAcquisitionTimestamp(date) === ts) {
            best = index;
            bestDelta = -1;
            return;
        }
        if (bestDelta === -1) return;
        const delta = Math.abs(date.getTime() - wanted.getTime());
        if (delta < bestDelta) {
            best = index;
            bestDelta = delta;
        }
    });
    return best;
}

/** The `?x=&y=` pair of a deep link, or null when either is missing/invalid. */
export function parseFocus(
    x: string | null,
    y: string | null,
): ViewerFocus | null {
    if (x === null || y === null) return null;
    const px = Number(x);
    const py = Number(y);
    if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
    return { x: px, y: py };
}
